import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import {
  SMS,
  channelCountPhrase,
  sentCountPhrase,
  shortChannelLabel,
  smsCostNote,
  unreachableNote,
  type ChannelAudience,
} from '../../../lib/broadcastChannels';
import { audienceLabel, statusColor } from './audience';
import type { GroupBroadcastResult } from '../../../types/groups';

/**
 * One channel's audience, on its own line.
 *
 * Deliberately never summed with the other channel: "60 guests" read by a host
 * who means email is how a list nobody inspected gets texted.
 */
function ChannelPlanCard({ plan, variant }: { plan: ChannelAudience; variant: PlanVariant }) {
  const unreachable = unreachableNote(plan);
  const cost = smsCostNote(plan);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, flex: 1, minWidth: 220 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          label={shortChannelLabel(plan.channel)}
          color={plan.channel === SMS ? 'warning' : 'default'}
        />
        <Typography variant="body2" fontWeight={700}>
          {variant === 'sent' ? sentCountPhrase(plan) : channelCountPhrase(plan)}
        </Typography>
      </Stack>
      {unreachable ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {unreachable}
        </Typography>
      ) : null}
      {cost ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {cost}
        </Typography>
      ) : null}
      {plan.failed > 0 ? (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
          {plan.failed} failed
        </Typography>
      ) : null}
    </Paper>
  );
}

/** 'audience' is who a send would reach; 'sent' is who it was handed off to, not who it arrived for. */
type PlanVariant = 'audience' | 'sent';

export function ChannelPlanSummary({
  plans,
  variant = 'audience',
}: {
  plans: ChannelAudience[];
  variant?: PlanVariant;
}) {
  if (plans.length === 0) return null;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
      {plans.map((plan) => <ChannelPlanCard key={plan.channel} plan={plan} variant={variant} />)}
    </Stack>
  );
}

/**
 * Per-guest outcomes. The aggregate chip stays for the at-a-glance read, but
 * each channel gets its own chip underneath so a guest can read "email sent,
 * SMS skipped — no phone" instead of one status that has to lie about one of
 * the two.
 */
export function ResultList({ results }: { results: GroupBroadcastResult[] }) {
  return (
    <Stack spacing={1}>
      {results.map((result) => (
        <Paper key={result.inviteeId} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
            <Box>
              <Typography variant="body2" fontWeight={700}>{result.fullName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {[result.email, result.phone].filter(Boolean).join(' · ') || 'No contact info'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="flex-start" flexWrap="wrap" useFlexGap>
              {result.segment ? <Chip size="small" variant="outlined" label={audienceLabel(result.segment)} /> : null}
              {(result.channelResults ?? []).length > 0 ? (
                (result.channelResults ?? []).map((outcome) => (
                  <Chip
                    key={outcome.channel}
                    size="small"
                    label={`${shortChannelLabel(outcome.channel)}: ${outcome.status}`}
                    color={statusColor(outcome.status)}
                    title={outcome.error || undefined}
                  />
                ))
              ) : (
                <Chip size="small" label={result.status} color={statusColor(result.status)} />
              )}
            </Stack>
          </Stack>
          {result.message ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{result.message}</Typography>
          ) : null}
          {result.error ? <Typography variant="caption" color="error">{result.error}</Typography> : null}
        </Paper>
      ))}
    </Stack>
  );
}
